import React from "react";
import {FilterSchema} from "./287/tables/UsersTable";
import {AnimationTrajectory} from "@visx/react-spring/lib/types";
import {XYChartTheme} from "@visx/xychart";
import {KeyOfGlyphs} from "./components/charts/XY/XyControls";
import {KeyOfIcons, KeyOfTimePeriods} from "./components/CONSTANTS";

export type Primitive = string | number | boolean | null;
export type PrimitiveObject = {
    [key: string]: Primitive;
};

export type NormalObject = {
    [key: string]: string | number;
};


export const fieldTypes = [
    "number", "string", "boolean", "date", "time", "datetime",
    "tel", "email", "url", "map", "image", "json", "ref", "custom1"
] as const;

export type FieldTypeInfo = {
    label: string;
    description: string;
    category: "basic" | "advanced" | "masked";
    color: string;
};

export const FIELD_TYPE_INFO: Record<FieldType, FieldTypeInfo> = {
    number: {label: "Number", description: "Number 🔢", category: "basic", color: "#007bff"},
    string: {label: "Text", description: "Text 📑", category: "basic", color: "#a772ff"},
    boolean: {label: "Boolean", description: "True/False ✅", category: "basic", color: "#fd7e14"},
    date: {label: "Date", description: "Date 📅", category: "basic", color: "#28a745"},
    time: {label: "Time", description: "Time ⏲", category: "basic", color: "#20c997"},
    datetime: {label: "Datetime", description: "Date & time 📅⌛", category: "basic", color: "#17a2b8"},
    tel: {label: "Phone", description: "Phone ☎", category: "masked", color: "#13013e"},
    email: {label: "Email", description: "Email 📩", category: "masked", color: "#e83e8c"},
    url: {label: "URL", description: "Link 🔗", category: "masked", color: "#fdc500"},
    map: {label: "Map", description: "Map 🗺", category: "advanced", color: "#f477de"},
    image: {label: "Image", description: "Image 📁", category: "masked", color: "#dc3545"},
    json: {label: "JSON", description: "JSON 👩‍💻", category: "masked", color: "#d6ba1b"},
    ref: {label: "REF", description: "Ref entity", category: "advanced", color: "#5e031b"},
    custom1: {label: "Custom", description: "Custom logic field", category: "masked", color: "#ffc107"},
};

export type FieldType = (typeof fieldTypes)[number];

export type KeyOfField = keyof Field;

export type OptionalKeys<T> = {
    [K in keyof T]-?: undefined extends T[K] ? K : never
}[keyof T];

//export type FieldMap = Record<string, Record<string, string | React.JSX.Element>>;

export type Strings = { [key: string]: string };

export type FieldMapValue = {
    rawValue: string | number
    lang: Strings
    color?: string
    icon?: KeyOfIcons
};

export type FieldMapGroups = {
    label: Strings,
    values: string[]
};

export interface FieldMap {
    groups?: FieldMapGroups[]
    multiValue?: true
    backgroundColor?: string
    async?: string
    values: {
        [key: string]: FieldMapValue
    }
}

export interface SelectOption {
    readonly value: string;
    readonly label: Strings;
    readonly color?: string;
}

export interface OptionI {
    readonly value: string;
    readonly label: string;
    readonly color?: string;
}

export const plainOption = (value: string) => ({value: value, label: value}) as OptionI

export interface GroupSelectOption {
    label: Strings,
    options: SelectOption[]
}

export interface GroupOptionI {
    label: string,
    options: OptionI[]
}

export interface GetColumnValueFuncI {
    (
        value: any,
        schema: Field,
        forHeader?: boolean,
        record?: PrimitiveObject | null
    ): string | React.JSX.Element;
}

export type FieldHeadAlignU = "left" | "center" | "right";
export type Field = {
    key: string;
    title: Strings;
    type: FieldType;
    detailed?: boolean;
    inline?: boolean;
    inlineEditable?: boolean;
    applyTransform?: boolean;
    headAlign?: FieldHeadAlignU;
    shortHead?: Strings
    comment?: string,
    ref?: RefField,
    searchRef?: SearchRef[]
} & FieldTypeSpecialProps & FieldBehaviorProps;


export type FieldBehaviorProps = {
    editable?: boolean;
    nullable?: boolean,
    getColumnValue?: string
    required?: boolean;
    defaultVal?: number | string;
    updateSingleFieldPath?: string,
}

export type FieldTypeSpecialProps = {
    map?: FieldMap;
    jsonHeight?: number;
    imageMaxHeight?: number,
    imageMaxWidth?: number,
    datetimeFormat?: string
}

export enum SqlOperation {
    EQUAL = 'eq',
    NOT_EQUAL = 'neq',
    GREATER_THAN = 'gt',
    LESS_THAN = 'lt',
    GREATER_THAN_OR_EQUAL = 'gte',
    LESS_THAN_OR_EQUAL = 'lte',
    LIKE = 'like',
    NOT_LIKE = 'not_like',
    IN = 'in',
    NOT_IN = 'not_in',
    IS_NULL = 'is_null',
    IS_NOT_NULL = 'is_not_null',
    BETWEEN = 'between',
}

export const SqlOperationInfo = {
    [SqlOperation.EQUAL]: {
        sql: '=',
        en: 'Equal',
        ua: '='
    },
    [SqlOperation.NOT_EQUAL]: {
        sql: '!=',
        en: 'Not Equal',
        ua: '!='
    },
    [SqlOperation.GREATER_THAN]: {
        sql: '>',
        en: 'Greater Than',
        ua: '>'
    },
    [SqlOperation.LESS_THAN]: {
        sql: '<',
        en: 'Less Than',
        ua: '<'
    },
    [SqlOperation.GREATER_THAN_OR_EQUAL]: {
        sql: '>=',
        en: 'Greater Than or Equal',
        ua: '>='
    },
    [SqlOperation.LESS_THAN_OR_EQUAL]: {
        sql: '<=',
        en: 'Less Than or Equal',
        ua: '<='
    },
    [SqlOperation.LIKE]: {
        sql: 'LIKE',
        en: 'Like',
        ua: 'містить'
    },
    [SqlOperation.NOT_LIKE]: {
        sql: 'NOT_LIKE',
        en: 'Not Like',
        ua: 'НЕ містить'
    },
    [SqlOperation.IN]: {
        sql: 'IN',
        en: 'In',
        ua: 'будь-який з'
    },
    [SqlOperation.NOT_IN]: {
        sql: 'NOT IN',
        en: 'Not In',
        ua: 'жоден з'
    },
    [SqlOperation.IS_NULL]: {
        sql: 'IS NULL',
        en: 'Is Null',
        ua: 'є порожнім'
    },
    [SqlOperation.IS_NOT_NULL]: {
        sql: 'IS NOT NULL',
        en: 'Is Not Null',
        ua: 'не є порожнім'
    },
    [SqlOperation.BETWEEN]: {
        sql: 'BETWEEN',
        en: 'Between',
        ua: 'між'
    }
};

export const OpsCategories = {
    eq: [SqlOperation.EQUAL, SqlOperation.NOT_EQUAL],
    range: [
        SqlOperation.GREATER_THAN,
        SqlOperation.GREATER_THAN_OR_EQUAL,
        SqlOperation.LESS_THAN,
        SqlOperation.LESS_THAN_OR_EQUAL,
        SqlOperation.BETWEEN,
    ],
    null: [SqlOperation.IS_NULL, SqlOperation.IS_NOT_NULL],
    in: [SqlOperation.IN, SqlOperation.NOT_IN],
    like: [SqlOperation.LIKE, SqlOperation.NOT_LIKE],
} as const;

export type OpsGroups = keyof typeof OpsCategories;

export type FilterValueI =
    string
    | undefined
    | number
    | boolean
    | null
    | Array<string | number>
    | [string | number, string | number];

export interface Filter {
    field: string;
    filterOp: SqlOperation;
    value: FilterValueI;
}

export interface QueryOrder {
    field: string,
    way: "ASC" | "DESC"
}

export interface FilterInputI extends Filter {
    type: FieldType,
    locked?: boolean,
    nanoid: string
}

export interface FieldOperations {
    [key: string]: SqlOperation[];
}

export interface PjTa {
    projectName: string,
    tableName: string
}

export interface QueryParamsDTO {
    project: string;
    table: string;
    fields: string[];       // Array of field names
    pages: number;          // Number of pages
    pageIndex: number;      // Current page index
    filters?: Filter[];     // Array of Filter objects
    order?: {
        field: string,
        way: "ASC" | "DESC"
    }; // Object representing column names as keys and order directions as values
}

export interface TableI {
    backend: string;
    title: Strings;
    pk: string;
    forAdmin?: boolean
    hidden?: boolean
    defaultOrderedBy: string;
    defaultOrderedWay: "DESC" | "ASC"
    defaultPerPage: number
    orderByFields: { [p: string]: Strings };
    fields: Field[];
    fieldOperations?: FieldOperations,
    defaultFilters?: string,
    action?: string,
    fieldSets?: TableFieldSetSettingsI
}

export interface SchemaI {
    actName: string;
    backend: string;
    defaultTable: string;
    extraFields?: boolean;
    filterSchemas: { [p: string]: { [p: string]: FilterSchema } };
    tables: { [p: string]: TableI };
    refEntities?: { [p: string]: RefEntitiesI };
}

export interface RefEntitiesI {
    table: string,
    name: string,
    keys: string[]
    mods?: { [p: string]: FieldMods };
}

export type RefFieldLayout = "vertical-list" | "table";
export type RefField = {
    entity: string,
    condition: RefCondition[],
    order: QueryOrder,
    limit: number
    fetchMode: "lazy" | "superLazy",
    single?: boolean,
    keys?: string[]
    mods?: { [p: string]: FieldMods }
    horizontal?: boolean
    layout: RefFieldLayout
}

export type SearchRef = {
    table: string,
    condition: RefCondition[]
}

export type  RefCondition = {
    mainField: string,
    foreignField?: string,
    op: SqlOperation,
    staticValues?: FilterValueI
}

//TODO modification of field props
type FieldMods = {

}

export interface TableFieldSetSettingsI {
    skipAll?: boolean,
    order: string[],
    sets: Record<string, TableFieldSetI>
}

export interface TableFieldSetI {
    name: Strings,
    shortName?: Strings,
    userDefined?: boolean,
    fields: {
        key: string,
        forceInline?: boolean,
        forceNonInline?: boolean
    }[]
}

export type ChartParamsI = {
    periods: KeyOfTimePeriods[]
    selectedPeriod?: KeyOfTimePeriods,
    selectedInterval?: KeyOfTimePeriods,
    filters?: {
        [key: string]: {
            lang: Strings,
            key: string,
            type: FieldType,
            value?: any
        }
    }
};


export type ChartEntry = {
    key: string,
    hide?: boolean,
    lang: Strings
};

export type ChartTypes = XyChartI | PieChartI

export type PaneType = "xyChart" | 'pieChart' | 'weatherWidget' | 'none'

export type ChartInternalTypes = "xy" | "pie";

export interface ChartI<T = ChartTypes> {
    id: string;
    lang: Strings
    originId?: string;
    queryId: string,
    type: ChartInternalTypes
    height?: number
    settings: T,
    parameters: ChartParamsI
}

interface SharedChartSettings {
    theme?: XYChartTheme
    colors?: Strings,
}

export type GlyphI = ChartEntry & { value: string }

export type KeyOfXyChartI = keyof XyChartI;

export interface XyChartI extends SharedChartSettings {
    dateKey?: string;
    xAxisName?: string;
    yAxisName?: string;
    entries: ChartEntry[];
    showEntries?: string[];
    lineType?: 'line' | 'area' | 'areastack' | 'none'
    curveShape?: 'linear' | 'cardinal' | 'step'
    barType?: 'bar' | 'barstack' | 'bargroup' | 'none',
    stackOffset?: 'wiggle' | 'expand' | 'diverging' | 'silhouette'
    renderHorizontally?: boolean
    hideGlyphs?: boolean
    glyphs: {
        key: string,
        hide?: boolean,
        lang?: Strings
        value: KeyOfGlyphs | string
    }[]
    sharedTooltip?: boolean
    animationTrajectory?: AnimationTrajectory
    useAnimatedComponents?: boolean
    gridProps?: [boolean, boolean]
    xAxisOrientation?: 'top' | 'bottom'
    yAxisOrientation?: 'left' | 'right'
    showTooltip?: boolean
    showVerticalCrosshair?: boolean
    showHorizontalCrosshair?: boolean
    snapTooltipToDatumX?: boolean
    snapTooltipToDatumY?: boolean
    tooltipGlyphComponent?: KeyOfGlyphs | string

    supportPie?: boolean
    usePie?: boolean
    pieShortNames?: Strings,
    usePieNumberLabel?: boolean
    usePieNumberPercentage?: boolean
    pieWhole?: boolean
}

export interface PieChartI extends SharedChartSettings {
    entries: ChartEntry[];
    glyphs: {
        key: string,
        hide?: boolean,
        lang?: Strings
        value: KeyOfGlyphs | string
    }[],
    pieShortNames?: Strings,
    usePieNumberLabel?: boolean,
    usePieNumberPercentage?: boolean
    pieWhole?: boolean,
    dynamicEntries?: boolean
}


export type IsLiteralUnion<T> = T extends string
    ? string extends T
        ? false
        : true
    : false;

export type BooleanKeys<T> = {
    [K in keyof T]: T[K] extends boolean ? K : never;
}[keyof T];

export type EnumKeys<T> = {
    [K in keyof T]: IsLiteralUnion<T[K]> extends true ? K : never;
}[keyof T];

export type PrimitiveKeys<T> = {
    [K in keyof T]:
    T[K] extends boolean ? never :
        IsLiteralUnion<T[K]> extends true ? never :
            T[K] extends string | number ? K : never;
}[keyof T];
